import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Payout, type PayoutStats, type PayoutStatus } from '@/lib/adminApi';
import {
  Card,
  DataTable,
  Spinner,
  EmptyState,
  Badge,
  StatTile,
  type Column,
  type BadgeTone,
} from '@/components/ui';
import { IconReceipt } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { PayoutKYCControl } from '@/components/admin/PayoutKYCControl';

const PAGE = 25;

// Payout status → badge tone. In-flight is informational; settled is ok; a
// failed/returned payout (money never left → refunded) is a warning. Keep in
// sync with admin-svc's payoutStatusName.
const STATUS_TONE: Record<PayoutStatus, BadgeTone> = {
  PENDING: 'info',
  CREATED: 'info',
  SETTLED: 'ok',
  FAILED: 'danger',
  RETURNED: 'warn',
};

// The status filter chips, in display order. '' = all.
const STATUS_FILTERS: Array<{ value: '' | PayoutStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CREATED', label: 'Processing' },
  { value: 'SETTLED', label: 'Settled' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'RETURNED', label: 'Returned' },
];

/** Compact AUD formatter ($ + 2dp). */
function aud(n: number): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

function statusBadge(p: Payout) {
  return <Badge tone={STATUS_TONE[p.status] ?? 'neutral'}>{p.status_name || p.status}</Badge>;
}

function methodLabel(m: string): string {
  if (m === 'payid') return 'PayID';
  if (m === 'bsb') return 'Bank (BSB)';
  return m || '—';
}

/** Payouts — Azupay driver earnings cash-outs, read from wallet-svc's
 *  driver_payouts (wallet-svc OWNS the money; admin-svc only READS through the
 *  shared pool). DEFENSIVE: shows a clean "not yet in Postgres" state when the
 *  payout table isn't in this DB. The drawer carries the payout-KYC verify
 *  control (gated by payouts.kyc_verify) — currently a clean "not wired yet"
 *  since the owning service has no verify endpoint. Covers the Filament
 *  AzupayPayout list/view. */
export function Payouts() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | PayoutStatus>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Payout | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'payouts', q, status, offset],
    queryFn: ({ signal }) =>
      adminApi.payouts({ q, status: status || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<Payout>> = [
    { key: 'status', header: 'Status', cell: (r) => statusBadge(r) },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.driver_name}</div>
          <div className="metric text-[11px] text-muted">#{r.driver_id}</div>
        </div>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      cell: (r) => <span className="text-xs text-slate-300">{methodLabel(r.payment_method)}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-slate-100">{aud(r.amount)}</span>,
    },
    {
      key: 'net',
      header: 'Net paid',
      align: 'right',
      cell: (r) => (
        <span className="metric text-sm text-ok" title={`fee ${aud(r.platform_fee)}`}>
          {aud(r.net_amount)}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'Requested',
      cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Payouts"
        subtitle="Driver earnings cash-outs via Azupay (PayID / bank transfer). Open one for detail and the payout-KYC review."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by Azupay id / driver…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Driver payouts not yet in Postgres"
            description={
              data?.note ??
              'The driver payout ledger is owned by wallet-svc and is not in this Postgres yet. This page lights up automatically once it lands.'
            }
            icon={<IconReceipt className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {data?.stats ? <PayoutStatsRow stats={data.stats} /> : null}

          <div className="mb-3 flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map((f) => {
              const active = f.value === status;
              return (
                <button
                  key={f.value || 'all'}
                  type="button"
                  onClick={() => {
                    setStatus(f.value);
                    setOffset(0);
                  }}
                  className={
                    active
                      ? 'rounded-md bg-brand-500/20 px-2.5 py-1 text-xs font-medium text-brand-300 ring-1 ring-inset ring-brand-500/40'
                      : 'rounded-md bg-ink-850 px-2.5 py-1 text-xs font-medium text-slate-300 ring-1 ring-inset ring-ink-700 hover:bg-ink-800'
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading payouts…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load payouts" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<Payout>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No payouts"
                      description="No driver payouts match this filter."
                      icon={<IconReceipt className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <PayoutDrawer payout={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** The headline totals above the table: in-flight, settled out, failed, fees. */
function PayoutStatsRow({ stats }: { stats: PayoutStats }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="In flight" value={String(stats.pending_count)} hint="pending + processing" />
      <StatTile
        label="Settled"
        value={aud(stats.settled_dollars)}
        hint={`${stats.settled_count} payout${stats.settled_count === 1 ? '' : 's'}`}
      />
      <StatTile
        label="Failed / returned"
        value={String(stats.failed_count)}
        hint="refunded to wallet"
      />
      <StatTile label="Platform fees" value={aud(stats.fees_dollars)} hint="on settled payouts" />
    </div>
  );
}

/** PayoutDrawer — the full payout detail + the payout-KYC verify control. The
 *  list row already carries every field, so no extra fetch is needed for the
 *  body. */
function PayoutDrawer({ payout: p, onClose }: { payout: Payout; onClose: () => void }) {
  return (
    <Drawer
      title={`Payout #${p.id}`}
      subtitle={`${p.driver_name} · ${p.status_name || p.status}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <Field label="Status" value={statusBadge(p)} />
          <Field label="Driver" value={`${p.driver_name} (#${p.driver_id})`} />
          {p.phone ? <Field label="Phone" value={<span className="metric">{p.phone}</span>} /> : null}
          {p.email ? <Field label="Email" value={p.email} /> : null}
          <Field label="Method" value={methodLabel(p.payment_method)} />
          <Field label="Amount (gross)" value={<span className="metric">{aud(p.amount)}</span>} />
          <Field label="Platform fee" value={<span className="metric text-muted">−{aud(p.platform_fee)}</span>} />
          <Field label="Net paid" value={<span className="metric text-ok">{aud(p.net_amount)}</span>} />
          {p.azupay_payment_id ? (
            <Field label="Azupay id" value={<span className="metric text-xs">{p.azupay_payment_id}</span>} />
          ) : null}
          {p.failure_reason ? <Field label="Failure reason" value={<span className="text-danger">{p.failure_reason}</span>} /> : null}
          <Field label="Requested" value={dateTime(p.created_at)} />
          {p.settled_at ? <Field label="Settled" value={dateTime(p.settled_at)} /> : null}
        </div>

        {/* Payout-KYC review for this payout's driver (gated; currently a clean
            "not wired yet" until the owning service grows the verify endpoint). */}
        <PayoutKYCControl driverId={p.driver_id} />
      </div>
    </Drawer>
  );
}
